<template>
    <div class="d-row project-body">
        <TileCard id="project-table" :loaded="settingsStore.getServicesLoaded">
            <template #body>
                <ServiceTable :data="settingsStore.getServices" @detail="loadInvoice" @teams="loadTeamsReports">
                </ServiceTable>
            </template>
        </TileCard>
        <TileCard id="invoice" :loaded="reportStore.isInvoiceLoaded" v-if="state.showInvoice">
            <template #body>
                <TSInvoiceVue class="invoice-body" :data="reportStore.getInvoiceData" :year="state.invoice.year"
                    :service="settingsStore.getServices.find(a => a.id == state.serviceId)"
                    :month="state.invoice.month + 1"></TSInvoiceVue>
            </template>
            <template #header>
                <div class="d-row">
                    <VueDatePicker v-model="state.invoice" month-picker :clearable="false" :teleport="true"
                        @update:model-value="loadInvoice(state.serviceId)">
                    </VueDatePicker>
                    <div class="buttons"><button class="accent" @click="createPDF"
                            :disabled="state.pdfGenerating">Generate
                            PDF</button></div>
                </div>
            </template>
        </TileCard>
        <TileCard id="project-table" :loaded="true" v-else-if="state.showServiceTeams">
            <template #body>
                <ServiceTeamsReport :data="state.serviceTeams"></ServiceTeamsReport>
            </template>
            <template #header>
                <div class="d-row">
                    <VueDatePicker v-model="state.invoice" month-picker :clearable="false" :teleport="true"
                        @update:model-value="loadInvoice(state.serviceId)">
                    </VueDatePicker>
                </div>
            </template>            
        </TileCard>
    </div>
</template>

<script setup lang="ts">
import TileCard from "@/components/util/TileCard.vue";
import ServiceTable from "@/components/tables/ServiceTable.vue";
import TSInvoiceVue from "@/components/report/TSInvoice.vue";
import { useReportStore } from "@/stores/report";
import { useSettingsStore } from "@/stores/setting";
import { closeToast, showLoading, showToastError } from "@/toastification";
import jsPDF from "jspdf";
import { onMounted, reactive } from "vue";
import ServiceTeamsReport from "@/components/service_teams/ServiceTeamsReport.vue";
import { useServiceTeamsStore } from "@/stores/serviceTeams";
import { ServiceTeamItem } from "@/services/entities/ServiceTeams/ServiceTeamItem";

const state = reactive({
    invoice: { year: (new Date()).getFullYear(), month: (new Date()).getMonth() },
    showInvoice: false,
    showServiceTeams: false,
    serviceId: 0,
    pdfGenerating: false,
    serviceTeams: [] as ServiceTeamItem[],
});

const settingsStore = useSettingsStore();
const reportStore = useReportStore();
const teamsStore = useServiceTeamsStore();

onMounted(async () => {
    await settingsStore.loadServices();
    await teamsStore.loadServiceTeams();
})

async function loadInvoice(serviceId: number) {
    state.showServiceTeams = false;
    state.showInvoice = true;
    state.serviceId = serviceId;
    await reportStore.loadInvoiceData(serviceId, state.invoice.year, state.invoice.month + 1);
}

async function loadTeamsReports(serviceId: number) {
    state.showInvoice = false;
    state.showServiceTeams = true;
    state.serviceId = serviceId;
    state.serviceTeams = teamsStore.getServiceTeams.filter(st => st.service.id === serviceId);
}

const createPDF = async () => {
    state.pdfGenerating = true;
    const loadToast = showLoading("Generating PDF", 0);
    const pdf = new jsPDF({
        unit: 'px',
        format: [680, 930]
    });

    try {
        let i = 0;
        while (true) {
            const page = document.getElementById(`page_${i}`);
            if (!page) {
                break;
            }
            if (i > 1) {
                pdf.addPage();
            }
            await pdf.html(page, { y: 930 * i });
            i++;
        }
        pdf.save(`${reportStore.getInvoiceData.service.blpgCode}_${state.invoice.year}_${state.invoice.month + 1}.pdf`);
    } catch (error) {
        console.error(error);
        showToastError(`Error generating PDF: ${error}`);
    } finally {
        state.pdfGenerating = false;
        closeToast(loadToast);
    }
}

</script>

<style scoped lang="scss">
#invoice, #project-table {
    overflow: auto;
    height: calc(100vh - 240px);
}

.project-body {
    align-content: start;
    margin-top: 10px;
}
</style>